/**
 * AIRTABLE SERVICE
 * 
 * Direct Airtable API integration for importing collections with photos.
 * Fetches fresh attachment URLs at import time to avoid expiration issues.
 * 
 * Security notes:
 * - API key is used client-side only, never sent to our servers
 * - Key is used once for import, then discarded (not stored)
 * - Read-only access - we only fetch data, never modify
 * - User chooses exactly which base/table to import
 */

const AIRTABLE_API_BASE = 'https://api.airtable.com/v0';

export interface AirtableBase {
  id: string;
  name: string;
  permissionLevel: string;
}

export interface AirtableTable {
  id: string;
  name: string;
  primaryFieldId: string;
  fields: AirtableField[];
}

export interface AirtableField {
  id: string;
  name: string;
  type: string;
  options?: any;
}

export interface AirtableAttachment {
  id: string;
  url: string;
  filename: string;
  size: number;
  type: string;
  width?: number;
  height?: number;
  thumbnails?: {
    small: { url: string; width: number; height: number };
    large: { url: string; width: number; height: number };
    full?: { url: string; width: number; height: number };
  };
}

export interface AirtableRecord {
  id: string;
  createdTime: string;
  fields: Record<string, any>;
}

export interface AirtableFetchResult {
  records: AirtableRecord[];
  offset?: string; // For pagination
}

/**
 * Validate an Airtable API key by attempting to list bases
 */
export async function validateApiKey(apiKey: string): Promise<{ valid: boolean; error?: string }> {
  try {
    const response = await fetch(`${AIRTABLE_API_BASE}/meta/bases`, {
      headers: {
        'Authorization': `Bearer ${apiKey}`,
      },
    });
    
    if (response.ok) {
      return { valid: true };
    } else if (response.status === 401) {
      return { valid: false, error: 'Invalid API key' };
    } else if (response.status === 403) {
      return { valid: false, error: 'API key does not have permission to list bases' };
    } else {
      return { valid: false, error: `Airtable returned status ${response.status}` };
    }
  } catch (error) {
    return { valid: false, error: 'Failed to connect to Airtable' };
  }
}

/**
 * List all bases the API key has access to
 */
export async function listBases(apiKey: string): Promise<AirtableBase[]> {
  const response = await fetch(`${AIRTABLE_API_BASE}/meta/bases`, {
    headers: {
      'Authorization': `Bearer ${apiKey}`,
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to list bases: ${response.status}`);
  }
  
  const data = await response.json();
  return data.bases || [];
}

/**
 * Get schema (tables and fields) for a specific base
 */
export async function getBaseSchema(apiKey: string, baseId: string): Promise<AirtableTable[]> {
  const response = await fetch(`${AIRTABLE_API_BASE}/meta/bases/${baseId}/tables`, {
    headers: {
      'Authorization': `Bearer ${apiKey}`,
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to get base schema: ${response.status}`);
  }
  
  const data = await response.json();
  return data.tables || [];
}

/**
 * Fetch records from a table (with pagination support)
 */
export async function fetchRecords(
  apiKey: string,
  baseId: string,
  tableId: string,
  options?: {
    offset?: string;
    pageSize?: number;
    filterByFormula?: string;
    sort?: { field: string; direction: 'asc' | 'desc' }[];
  }
): Promise<AirtableFetchResult> {
  const params = new URLSearchParams();
  
  if (options?.offset) {
    params.set('offset', options.offset);
  }
  if (options?.pageSize) {
    params.set('pageSize', String(Math.min(options.pageSize, 100))); // Max 100 per request
  }
  if (options?.filterByFormula) {
    params.set('filterByFormula', options.filterByFormula);
  }
  if (options?.sort) {
    options.sort.forEach((s, i) => {
      params.set(`sort[${i}][field]`, s.field);
      params.set(`sort[${i}][direction]`, s.direction);
    });
  }
  
  const url = `${AIRTABLE_API_BASE}/${baseId}/${tableId}?${params.toString()}`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${apiKey}`,
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch records: ${response.status}`);
  }
  
  const data = await response.json();
  return {
    records: data.records || [],
    offset: data.offset,
  };
}

/**
 * Fetch ALL records from a table (handles pagination automatically)
 */
export async function fetchAllRecords(
  apiKey: string,
  baseId: string,
  tableId: string,
  onProgress?: (fetched: number) => void
): Promise<AirtableRecord[]> {
  const allRecords: AirtableRecord[] = [];
  let offset: string | undefined;
  
  do {
    const result = await fetchRecords(apiKey, baseId, tableId, {
      offset,
      pageSize: 100,
    });
    
    allRecords.push(...result.records);
    offset = result.offset;
    
    onProgress?.(allRecords.length);
    
    // Small delay to respect rate limits (5 requests per second)
    if (offset) {
      await new Promise(resolve => setTimeout(resolve, 250));
    }
  } while (offset);
  
  return allRecords;
}

/**
 * Extract attachment URLs from a record field
 */
export function extractAttachments(fieldValue: any): AirtableAttachment[] {
  if (!fieldValue || !Array.isArray(fieldValue)) {
    return [];
  }
  
  return fieldValue.filter(item => 
    item && typeof item === 'object' && item.url
  ) as AirtableAttachment[];
}

/**
 * Detect which fields contain attachments in a set of records
 */
export function detectAttachmentFields(records: AirtableRecord[]): string[] {
  const attachmentFields = new Set<string>();
  
  for (const record of records.slice(0, 50)) { // Check first 50 records
    for (const [fieldName, value] of Object.entries(record.fields)) {
      if (Array.isArray(value) && value.length > 0) {
        const firstItem = value[0];
        if (firstItem && typeof firstItem === 'object' && firstItem.url && firstItem.filename) {
          attachmentFields.add(fieldName);
        }
      }
    }
  }
  
  return Array.from(attachmentFields);
}

/**
 * Analyze records to suggest field mappings
 */
export function analyzeRecords(records: AirtableRecord[], tables: AirtableTable[], tableId: string): {
  fields: { name: string; type: string; sampleValues: string[]; suggestedMapping: string | null }[];
  attachmentFields: string[];
  recordCount: number;
} {
  const table = tables.find(t => t.id === tableId || t.name === tableId);
  const fieldInfo: Record<string, { type: string; samples: Set<string> }> = {};
  
  // Initialize from schema if available
  if (table) {
    for (const field of table.fields) {
      fieldInfo[field.name] = { type: field.type, samples: new Set() };
    }
  }
  
  // Collect sample values
  for (const record of records.slice(0, 20)) {
    for (const [fieldName, value] of Object.entries(record.fields)) {
      if (!fieldInfo[fieldName]) {
        fieldInfo[fieldName] = { type: 'unknown', samples: new Set() };
      }
      
      // Get string representation for samples
      if (value !== null && value !== undefined) {
        if (typeof value === 'string') {
          fieldInfo[fieldName].samples.add(value);
        } else if (typeof value === 'number' || typeof value === 'boolean') {
          fieldInfo[fieldName].samples.add(String(value));
        } else if (Array.isArray(value)) {
          if (value[0]?.url) {
            // Attachment array
            fieldInfo[fieldName].samples.add(`[${value.length} attachment(s)]`);
            fieldInfo[fieldName].type = 'multipleAttachments';
          } else if (isLinkedRecordObjectArray(value)) {
            // Linked record objects - extract and show names
            const names = extractLinkedRecordNames(value);
            fieldInfo[fieldName].samples.add(names.join(', '));
            fieldInfo[fieldName].type = 'multipleRecordLinks';
          } else if (isLinkedRecordArray(value)) {
            // Linked record IDs - show as "[X linked record(s)]"
            fieldInfo[fieldName].samples.add(`[${value.length} linked record(s)]`);
            fieldInfo[fieldName].type = 'multipleRecordLinks';
          } else {
            // Regular array - join values
            fieldInfo[fieldName].samples.add(value.join(', '));
          }
        }
      }
    }
  }
  
  // Detect attachment fields
  const attachmentFields = detectAttachmentFields(records);
  
  // Build result with suggested mappings
  const fields = Object.entries(fieldInfo).map(([name, info]) => {
    const lowerName = name.toLowerCase();
    let suggestedMapping: string | null = null;
    
    // Suggest mappings based on field name
    if (lowerName.includes('brand') || lowerName.includes('maker')) {
      suggestedMapping = 'brand';
    } else if (lowerName === 'name' || lowerName.includes('polish name') || lowerName.includes('color name')) {
      suggestedMapping = 'name';
    } else if (lowerName === 'swatched' || lowerName === 'swatched?') {
      // Exact match for "swatched" field (not "swatch photos")
      suggestedMapping = 'swatched';
    } else if (lowerName.includes('photo') || lowerName.includes('image') || lowerName.includes('swatch') || lowerName.includes('attachment')) {
      suggestedMapping = 'photos';
    } else if (lowerName.includes('collection')) {
      suggestedMapping = 'collection';
    } else if (lowerName === 'line' || lowerName.includes('product line') || lowerName.includes('sub-brand') || lowerName.includes('sub brand')) {
      suggestedMapping = 'line';
    } else if (lowerName.includes('swatch number') || lowerName.includes('swatch #') || lowerName.includes('swatch num')) {
      suggestedMapping = 'swatchNumber';
    } else if (lowerName.includes('color') || lowerName.includes('colour') || lowerName.includes('description')) {
      suggestedMapping = 'colorDescription';
    } else if (lowerName.includes('note')) {
      suggestedMapping = 'notes';
    } else if (lowerName.includes('tag')) {
      suggestedMapping = 'tags';
    } else if (lowerName.includes('finish') || lowerName.includes('effect')) {
      suggestedMapping = 'finish';
    } else if (lowerName.includes('release') || lowerName.includes('type')) {
      suggestedMapping = 'releaseType';
    } else if (lowerName.includes('year') || lowerName.includes('date')) {
      suggestedMapping = 'releaseYear';
    } else if (lowerName.includes('event') || lowerName.includes('ppu')) {
      suggestedMapping = 'eventSource';
    } else if (lowerName.includes('quantity') || lowerName.includes('qty')) {
      suggestedMapping = 'quantity';
    } else if (lowerName.includes('price') || lowerName.includes('cost')) {
      suggestedMapping = 'price';
    } else if (lowerName.includes('storage') || lowerName.includes('location')) {
      suggestedMapping = 'storage';
    } else if (lowerName.includes('favorite') || lowerName.includes('fav')) {
      suggestedMapping = 'isFavorite';
    } else if (lowerName.includes('wish')) {
      suggestedMapping = 'onWishlist';
    } else if (lowerName.includes('worn') || lowerName.includes('used') || lowerName.includes('usage') || lowerName.includes('times')) {
      suggestedMapping = 'timesUsed';
    }
    
    return {
      name,
      type: info.type,
      sampleValues: Array.from(info.samples).slice(0, 3),
      suggestedMapping,
    };
  });
  
  return {
    fields,
    attachmentFields,
    recordCount: records.length,
  };
}

/**
 * Download an image from Airtable attachment URL
 * Returns a Blob that can be uploaded to Firebase
 */
export async function downloadAttachment(attachment: AirtableAttachment): Promise<Blob> {
  // Use the full-size URL if available, otherwise the main URL
  const url = attachment.thumbnails?.full?.url || attachment.url;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to download attachment: ${response.status}`);
  }
  
  return response.blob();
}

/**
 * Helper to get the best quality image URL from an attachment
 */
export function getBestImageUrl(attachment: AirtableAttachment): string {
  // Prefer full thumbnail (usually best quality), then large, then original
  return attachment.thumbnails?.full?.url 
    || attachment.thumbnails?.large?.url 
    || attachment.url;
}

/**
 * Check if a value is an array of Airtable linked record IDs
 * Linked records come as arrays of strings starting with "rec"
 */
export function isLinkedRecordArray(value: any): boolean {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  
  // Check if first item is a string starting with "rec" (Airtable record ID format)
  // Also make sure it's NOT an attachment (which would have url/filename)
  const firstItem = value[0];
  return (
    typeof firstItem === 'string' && 
    firstItem.startsWith('rec') &&
    firstItem.length > 10 // Record IDs are typically longer
  );
}

/**
 * Check if a value is an array of linked record objects
 * Some Airtable configurations return linked records as objects with id/name properties
 */
export function isLinkedRecordObjectArray(value: any): boolean {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  
  const firstItem = value[0];
  // Check for object with id starting with "rec" OR object with name/title property
  return (
    typeof firstItem === 'object' &&
    firstItem !== null &&
    !firstItem.url && // Not an attachment
    (
      (firstItem.id && typeof firstItem.id === 'string' && firstItem.id.startsWith('rec')) ||
      firstItem.name ||
      firstItem.title
    )
  );
}

/**
 * Extract display names from linked record objects
 * Handles arrays like [{ id: "recXXX", name: "Collection Name" }]
 */
export function extractLinkedRecordNames(value: any[]): string[] {
  return value
    .map(item => {
      if (typeof item === 'string') {
        return item;
      }
      if (typeof item === 'object' && item !== null) {
        return item.name || item.title || item.value || String(item);
      }
      return String(item);
    })
    .filter(Boolean);
}

/**
 * Fetch specific records by their IDs from a table
 * Used to resolve linked record display names
 */
export async function fetchRecordsByIds(
  apiKey: string,
  baseId: string,
  tableId: string,
  recordIds: string[]
): Promise<Map<string, string>> {
  const recordMap = new Map<string, string>();
  
  if (recordIds.length === 0) {
    return recordMap;
  }
  
  // Airtable doesn't have a direct "get by IDs" endpoint, so we need to use filterByFormula
  // We'll batch these to avoid formula length limits
  const batchSize = 50;
  
  for (let i = 0; i < recordIds.length; i += batchSize) {
    const batch = recordIds.slice(i, i + batchSize);
    
    // Build OR formula: OR(RECORD_ID()="rec1", RECORD_ID()="rec2", ...)
    const formula = `OR(${batch.map(id => `RECORD_ID()="${id}"`).join(',')})`;
    
    try {
      const result = await fetchRecords(apiKey, baseId, tableId, {
        filterByFormula: formula,
        pageSize: 100,
      });
      
      // Map record ID to the primary field value (first non-empty field, usually the name)
      for (const record of result.records) {
        // Get the first text field value as the display name
        const displayName = getPrimaryFieldValue(record);
        if (displayName) {
          recordMap.set(record.id, displayName);
        }
      }
      
      // Small delay for rate limiting
      if (i + batchSize < recordIds.length) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
    } catch (error) {
      console.warn(`Failed to fetch linked records batch:`, error);
      // Continue with other batches even if one fails
    }
  }
  
  return recordMap;
}

/**
 * Get the primary display value from a record
 * Usually the first text field or Name field
 */
function getPrimaryFieldValue(record: AirtableRecord): string | null {
  const fields = record.fields;
  
  // Try common primary field names first
  const primaryNames = ['Name', 'name', 'Title', 'title', 'Brand', 'brand'];
  for (const name of primaryNames) {
    if (fields[name] && typeof fields[name] === 'string') {
      return fields[name];
    }
  }
  
  // Fall back to first string field
  for (const [_key, value] of Object.entries(fields)) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  
  return null;
}

/**
 * Detect linked record fields and identify which tables they link to
 * Returns a map of fieldName -> linkedTableId
 */
export function detectLinkedRecordFields(
  tables: AirtableTable[],
  tableId: string
): Map<string, string> {
  const linkedFields = new Map<string, string>();
  
  const table = tables.find(t => t.id === tableId || t.name === tableId);
  if (!table) return linkedFields;
  
  for (const field of table.fields) {
    // Airtable field types for linked records
    if (field.type === 'multipleRecordLinks' && field.options?.linkedTableId) {
      linkedFields.set(field.name, field.options.linkedTableId);
    }
  }
  
  return linkedFields;
}

/**
 * Build a cache of all linked record resolutions needed for import
 * This fetches all linked tables upfront to avoid repeated API calls during import
 */
export async function buildLinkedRecordCache(
  apiKey: string,
  baseId: string,
  records: AirtableRecord[],
  tables: AirtableTable[],
  tableId: string,
  onProgress?: (status: string) => void
): Promise<Map<string, Map<string, string>>> {
  // Map of tableId -> (recordId -> displayName)
  const cache = new Map<string, Map<string, string>>();
  
  // Detect which fields are linked records
  const linkedFields = detectLinkedRecordFields(tables, tableId);
  
  if (linkedFields.size === 0) {
    // No linked record fields detected from schema
    // Try to detect from actual data
    const detectedLinks = detectLinkedRecordFieldsFromData(records);
    if (detectedLinks.size === 0) {
      return cache;
    }
    
    // For detected links without schema info, we can't resolve them automatically
    // They'll fall back to showing record IDs or we could try to guess tables
    console.log('Detected linked fields from data but no schema info:', detectedLinks);
    return cache;
  }
  
  // Collect all record IDs per linked table
  const recordIdsByTable = new Map<string, Set<string>>();
  
  for (const record of records) {
    for (const [fieldName, linkedTableId] of linkedFields) {
      const value = record.fields[fieldName];
      if (isLinkedRecordArray(value)) {
        if (!recordIdsByTable.has(linkedTableId)) {
          recordIdsByTable.set(linkedTableId, new Set());
        }
        for (const recordId of value) {
          recordIdsByTable.get(linkedTableId)!.add(recordId);
        }
      }
    }
  }
  
  // Fetch display names for each linked table
  for (const [linkedTableId, recordIds] of recordIdsByTable) {
    if (recordIds.size === 0) continue;
    
    const linkedTable = tables.find(t => t.id === linkedTableId);
    const tableName = linkedTable?.name || linkedTableId;
    
    onProgress?.(`Resolving ${recordIds.size} linked records from "${tableName}"...`);
    
    const resolved = await fetchRecordsByIds(
      apiKey,
      baseId,
      linkedTableId,
      Array.from(recordIds)
    );
    
    cache.set(linkedTableId, resolved);
  }
  
  return cache;
}

/**
 * Detect linked record fields from actual data (fallback when schema doesn't help)
 */
function detectLinkedRecordFieldsFromData(records: AirtableRecord[]): Set<string> {
  const linkedFields = new Set<string>();
  
  for (const record of records.slice(0, 20)) {
    for (const [fieldName, value] of Object.entries(record.fields)) {
      if (isLinkedRecordArray(value)) {
        linkedFields.add(fieldName);
      }
    }
  }
  
  return linkedFields;
}

/**
 * Resolve a linked record field value to display names
 * Returns the resolved string(s) or the original value if resolution fails
 */
export function resolveLinkedRecordValue(
  value: any,
  fieldName: string,
  linkedFields: Map<string, string>,
  cache: Map<string, Map<string, string>>
): any {
  // If it's already a plain string, return as-is
  if (typeof value === 'string') {
    return value;
  }
  
  // Handle linked record objects - arrays like [{ id: "recXXX", name: "Collection Name" }]
  if (isLinkedRecordObjectArray(value)) {
    const names = extractLinkedRecordNames(value);
    if (names.length === 1) {
      return names[0];
    } else if (names.length > 1) {
      return names.join(', ');
    }
  }
  
  // Not a linked record ID array, return as-is
  if (!isLinkedRecordArray(value)) {
    // If it's an array of regular strings (not record IDs), join them
    if (Array.isArray(value) && value.every(item => typeof item === 'string' && !item.startsWith('rec'))) {
      return value.join(', ');
    }
    return value;
  }
  
  // Try to resolve using cache
  const linkedTableId = linkedFields.get(fieldName);
  if (!linkedTableId) {
    // Field not in schema as linked, return original (will show record IDs)
    // Try to extract any name properties if present
    if (Array.isArray(value)) {
      const extracted = value.map(v => {
        if (typeof v === 'object' && v !== null && (v.name || v.title)) {
          return v.name || v.title;
        }
        return v;
      });
      if (extracted.some(v => typeof v === 'string' && !v.startsWith('rec'))) {
        return extracted.filter(v => typeof v === 'string' && !v.startsWith('rec')).join(', ');
      }
    }
    return value;
  }
  
  const tableCache = cache.get(linkedTableId);
  if (!tableCache) {
    // No cache for this table, return original
    return value;
  }
  
  // Resolve each record ID to its display name
  const resolvedValues = (value as string[])
    .map(recordId => tableCache.get(recordId) || recordId)
    .filter(Boolean);
  
  // Return as single value if only one, otherwise join with comma
  if (resolvedValues.length === 1) {
    return resolvedValues[0];
  } else if (resolvedValues.length > 1) {
    return resolvedValues.join(', ');
  }
  
  return value;
}
